import styled, { css } from 'styled-components';
import {
  BodyText,
  BodyTextSmall,
  fontSize1,
  fontSize2,
  fontSize3,
  HeadingS,
  lineHeight1,
  lineHeight3,
} from 'lib/design-system';
import { IconLocationPin } from '@toolshed/styleguide/components/AppIcon/AppIcons';

import {
  breakpointMediumStart,
  neutral900,
  neutral600,
  default500,
  fontSizeLabelsRegular,
  fontWeightSemibold,
  spacing1,
  spacing2,
  spacing3,
  spacing4,
  spacing6,
  spacing9,
  spacing7,
} from 'lib/mortar';
import { Wrapper } from './TradeCardStatus';
import { ContactCtas } from 'lib/components/ContactCtas/ContactCtas';
import { ABBreakpointTabletStart } from 'lib/mortar/design-tokens/experimental';
import { colors } from '@checkatrade/web-ui/themes';
import type { Ref } from 'react';

export const CardWrapper = styled.div<StyledDiv>`
  ${Wrapper} {
    height: ${spacing9};
  }
`;

export const MainContent = styled.div<
  StyledDiv & {
    ref?: Ref<HTMLDivElement>;
  }
>`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  @media (min-width: ${ABBreakpointTabletStart}) {
    padding: ${spacing6};
  }
`;

export const Description = styled.div<StyledDiv>`
  color: ${neutral600};
  line-height: ${spacing6};
  font-size: ${fontSize2};
  grid-area: Description;
  padding: 0 ${spacing4};
  max-width: ${breakpointMediumStart};

  @media (min-width: ${ABBreakpointTabletStart}) {
    padding: 0;
  }
`;

export const SkillItemWrapper = styled.div<StyledDiv>`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: ${spacing2};
  column-gap: ${spacing3};

  span {
    font-size: ${fontSize1};
    color: ${default500};
    font-weight: ${fontWeightSemibold};
  }
`;

export const SkillItem = styled.div<StyledDiv>`
  column-gap: ${spacing2};
  display: flex;
  font-size: ${fontSize1};

  svg {
    margin-top: ${spacing1};
  }
`;

export const Name = styled(HeadingS)`
  color: ${neutral900};
  font-size: ${fontSize3};
`;

export const Location = styled(BodyTextSmall)`
  display: flex;
  align-items: center;
  color: ${neutral600};
  column-gap: ${spacing1};
  padding-right: ${spacing1};
`;

export const Distance = styled.span<StyledSpan>`
  color: ${neutral600};
`;

export const LocationPin = styled(IconLocationPin)`
  margin-right: ${spacing1};
`;

export const Rating = styled.div<StyledDiv>`
  display: flex;
  flex-direction: row;
  background: ${colors.accent[5]};
  border-radius: ${spacing1};
  padding: ${spacing1} ${spacing2};
  width: fit-content;
  height: fit-content;
  column-gap: ${spacing1};
  align-items: center;
`;

export const RatingScore = styled.span<StyledSpan>`
  font-size: ${fontSize3};
  font-weight: ${fontWeightSemibold};
  color: ${colors.secondary.altFg};
  line-height: ${lineHeight3};
`;

export const Reviews = styled.span<StyledSpan>`
  font-size: ${fontSizeLabelsRegular};
  color: ${colors.secondary.altFg};
  font-weight: ${fontWeightSemibold};
  line-height: ${lineHeight1};
`;

export const FirstReviewFauxLink = styled.span<StyledSpan>`
  display: inline-block;
  text-align: left;
  color: ${default500};
  font-size: ${fontSize2};
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

export const LogoAndInfoWrapper = styled.div<StyledDiv>`
  display: flex;
  column-gap: ${spacing3};
  padding: ${spacing7} ${spacing4} 0;
  grid-area: LogoAndInfoWrapper;

  @media (min-width: ${ABBreakpointTabletStart}) {
    padding: 0;
  }
`;

export const InfoWrapper = styled.div<StyledDiv>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  row-gap: ${spacing2};
`;

export const ContactCtasWrapper = styled(ContactCtas)`
  height: fit-content;
  align-self: flex-end;
  padding: ${spacing4};
  grid-area: ContactCtasWrapper;

  @media (min-width: ${ABBreakpointTabletStart}) {
    padding: 0;
    max-width: 300px;
    justify-self: flex-end;
  }
`;

export const LocationAndAvailabilityWrapper = styled.div<StyledDiv>`
  display: flex;
  flex-direction: column;
  row-gap: ${spacing2};
  justify-items: center;

  @media (min-width: ${breakpointMediumStart}) {
    flex-direction: row;
    column-gap: ${spacing2};
    margin-bottom: 0;
  }
`;

export const SearchCardGrid = styled.div<
  StyledDiv & {
    showSponsoredBadge: boolean;
    hasRecentlyReviewedBadge?: boolean;
  }
>`
  display: flex;
  cursor: pointer;
  flex-direction: column;
  width: 100%;
  flex-grow: 1;

  @media (min-width: ${ABBreakpointTabletStart}) {
    gap: ${spacing4};
    ${({ showSponsoredBadge, hasRecentlyReviewedBadge }) => css`
      display: grid;
      grid-template-areas:
        ${showSponsoredBadge && "'badge badge'"}
        'LogoAndInfoWrapper LogoAndInfoWrapper'
        ${hasRecentlyReviewedBadge && "'recent-badge recent-badge'"}
        'Description ContactCtasWrapper';
    `}
  }

  &,
  &:active,
  &:hover,
  &:link {
    text-decoration: none;
  }
`;

export const AvailabilityWrapper = styled.div<StyledDiv>`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const Availability = styled(BodyTextSmall)`
  color: ${neutral600};
  margin-left: ${spacing1};
`;

export const Badge = styled(BodyText)`
  grid-area: badge;

  @media (max-width: ${ABBreakpointTabletStart}) {
    padding: ${spacing4} ${spacing4} 0;
  }
`;
